/**
 * SOW narrative section splitting
 * ────────────────────────────────
 * Splits the edited SOW markdown (`deal_sow.sowContent`) into ordered sections
 * keyed by heading, so the Hybrid SOW export can route each section's prose into
 * the corresponding contract slot while injecting data-driven tables separately.
 *
 * Pure string logic (no document types), so it lives in shared and is unit-testable.
 */
/**
 * Normalize a heading into a stable slug for fuzzy matching.
 *
 * A leading section ordinal ("3.", "3)", "Section 3 -") is stripped FIRST so a
 * numbered heading and its bare form collapse to the same slug. This keeps the
 * slug chain symmetric across the three places it is derived independently and
 * must agree: the template-build side (heading promotion + narrative/data-table
 * collapse over the parsed docx) and the export side (splitting the per-deal SOW
 * markdown). Without it, "## 3. Scope of Work" -> "3_scope_of_work" never matches
 * a "scope_of_work" slot and the section silently renders empty.
 */
export function slugifyHeading(heading) {
    return heading
        .replace(/^\s*(?:section\s+)?\d+[.)]?\s*/i, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}
/**
 * Split markdown into sections at ATX headings (`#`..`######`). Content before
 * the first heading becomes a leading section with an empty heading/slug.
 *
 * `maxLevel` (default 6) bounds which headings are split points: a heading DEEPER
 * than `maxLevel` is kept inline as part of the current section's body (so e.g.
 * `#### Objective` under a `### Plan` section with maxLevel=3 stays inside the Plan
 * section rather than becoming its own slug). Used to keep a section's sub-headings
 * within its single render slot.
 */
export function splitMarkdownByHeadings(markdown, maxLevel = 6) {
    const lines = (markdown ?? '').replace(/\r\n?/g, '\n').split('\n');
    const sections = [];
    const preamble = [];
    let current = null;
    const flush = () => {
        if (!current)
            return;
        sections.push({
            heading: current.heading,
            slug: slugifyHeading(current.heading),
            level: current.level,
            body: current.lines.join('\n').trim(),
        });
        current = null;
    };
    for (const line of lines) {
        const match = line.match(/^(#{1,6})\s+(.*)$/);
        if (match && match[1].length <= maxLevel) {
            flush();
            current = { heading: (match[2] ?? '').trim(), level: match[1].length, lines: [] };
        }
        else if (current) {
            current.lines.push(line);
        }
        else {
            preamble.push(line);
        }
    }
    flush();
    const preambleBody = preamble.join('\n').trim();
    if (preambleBody) {
        sections.unshift({ heading: '', slug: '', level: 0, body: preambleBody });
    }
    return sections;
}
//# sourceMappingURL=sow-sections.js.map